import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, TriangleAlert, RefreshCw } from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { usePeriodDrilldown } from '../../hooks/usePeriodDrilldown';

// =============================================================================
// Finance › Cash Flow   (LIVE)
//
// INDIRECT METHOD, and that is a decision rather than a shortcut. It starts
// from profit before tax and adjusts: add back the costs that moved no cash,
// take off the cash tied up in working capital, then investing and financing.
//
// The direct method — receipts and payments modelled per customer and supplier
// — needs a debtors ageing and a payment profile per account. That is a great
// deal of machinery for a budget nobody manages at that grain, and it is why
// most budgeting tools that promise a direct cash flow ship a broken one.
//
// EVERY LINE COMES FROM SOMEWHERE THAT ALREADY EXISTS.
//   profit, depreciation, finance costs   vw_bud_gl_consolidated (the spine)
//   debtors, creditors                    payment terms where known, the
//                                         bud_working_capital assumption where
//                                         not — and the page says which
//   inventory                             calc_fg_cost x volumes, at cost
//   tax                                   bud_working_capital rate and lag
//   capex                                 bud_capex, at its CASH period
//   debt                                  bud_debt_instruments schedule
//
// DEPRECIATION IS ADDED BACK OFF THE LEDGER, not recomputed from the asset
// register. If this page recalculated it, the first journal against a
// depreciation account would make the P&L and the cash flow disagree and
// nothing on either screen would say so.
//
// SIGN: every line arrives with its cash effect already signed — in positive,
// out negative — so each subtotal is the plain sum of the lines above it and
// the column adds up by hand.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';

// Brackets, not minus signs: an accountant reads a bracket faster, and cash out
// is the thing on this page you most want to spot at a glance.
const fmtR = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '—';
  const s = Math.abs(v).toLocaleString();
  return v < 0 ? `(${s})` : s;
};

const T_SECTION = { bgCell: 'rgba(100, 116, 139, 0.13)', baseFontStyle: '600 12px' };
const T_SUBTOTAL = { bgCell: 'rgba(37, 99, 235, 0.13)', baseFontStyle: '700 12px' };
const T_GRAND = { bgCell: 'rgba(16, 185, 129, 0.18)', baseFontStyle: '700 13px' };
const T_GRAND_NEG = { bgCell: 'rgba(239, 68, 68, 0.18)', baseFontStyle: '700 13px' };
const T_BALANCE = { bgCell: 'rgba(100, 116, 139, 0.20)', baseFontStyle: '700 12px' };
const T_YEAR = { bgCell: 'rgba(100, 116, 139, 0.10)' };

const EMPTY = {
  structure: { sections: [], subtotals: [], balanceLines: [] },
  rows: [], cash: [], assumptions: null, interest: null, daysSources: [], unposted: [],
};

const sumOf = (row, periods) => periods.reduce((a, p) => a + (row.values[p - 1] || 0), 0);
const sumAll = (row) => row.values.reduce((a, b) => a + b, 0);

export default function CashFlow() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getCashFlow();
      setData(res.data || EMPTY);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the cash flow.');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  const sections = useMemo(() => data.structure?.sections || [], [data.structure]);
  const subtotals = useMemo(() => data.structure?.subtotals || [], [data.structure]);
  const balanceLines = useMemo(() => data.structure?.balanceLines || [], [data.structure]);

  // ---- Pivot: one API row per line per period -> one line of twelve ---------
  const lines = useMemo(() => {
    const byKey = new Map();
    (data.rows || []).forEach((r) => {
      const k = `${r.line_order}`;
      if (!byKey.has(k)) {
        byKey.set(k, {
          key: k,
          order: r.line_order,
          label: r.label,
          kind: r.kind,
          section: r.section,
          values: Array(12).fill(0),
        });
      }
      const row = byKey.get(k);
      if (r.financial_period >= 1 && r.financial_period <= 12) {
        row.values[r.financial_period - 1] += r.amount;
      }
    });
    return [...byKey.values()].sort((a, b) => a.order - b.order);
  }, [data.rows]);

  // Cash balances, indexed by period, for the two lines under the statement.
  const cashByPeriod = useMemo(() => {
    const m = new Map();
    (data.cash || []).forEach((c) => m.set(c.financial_period, c));
    return m;
  }, [data.cash]);

  const openingCash = useMemo(
    () => (data.cash || [])[0]?.opening_cash || 0,
    [data.cash]
  );

  // ---- Build the statement -------------------------------------------------
  //
  // A subtotal sums every line whose line_order falls in its range. Ranges
  // rather than a cumulative "everything above" because operating, investing
  // and financing are three independent blocks — a running total across all of
  // them would add capex to working capital and mean nothing.
  const buildStatement = useCallback((fold) => {
    const out = [];
    const sumRange = (from, to) => {
      const vals = Array(12).fill(0);
      lines.filter((l) => l.order >= from && l.order <= to)
        .forEach((l) => l.values.forEach((v, i) => { vals[i] += v; }));
      return vals;
    };

    sections.forEach((sec) => {
      const secLines = lines.filter((l) => l.section === sec.key);
      const subsHere = subtotals.filter((s) => s.after === sec.key);
      // A section with no lines AND no subtotal to draw is simply absent. A
      // tenant with no debt should not have an empty Financing heading.
      if (!secLines.length && !subsHere.length) return;

      if (secLines.length) {
        const vals = Array(12).fill(0);
        secLines.forEach((l) => l.values.forEach((v, i) => { vals[i] += v; }));
        out.push({
          kind: 'section', key: sec.key, label: sec.label, hint: sec.hint,
          count: secLines.length, values: vals, total: vals.reduce((a, b) => a + b, 0),
        });
        if (!fold[sec.key]) {
          secLines.forEach((l) => out.push({
            kind: 'line', key: l.key, label: l.label, tone: l.kind,
            values: l.values, total: sumAll(l),
          }));
        }
      }

      subsHere.forEach((s) => {
        const vals = sumRange(s.from, s.to);
        out.push({
          kind: 'subtotal', key: s.key, label: s.label, grand: !!s.grand,
          emphasis: !!s.emphasis, values: vals, total: vals.reduce((a, b) => a + b, 0),
        });
      });
    });

    // Opening and closing cash: BALANCES, drawn under the movements and styled
    // differently, because presenting a balance at the same weight as a
    // movement is how somebody ends up adding the two together.
    if (balanceLines.length && cashByPeriod.size) {
      const open = Array(12).fill(0);
      const close = Array(12).fill(0);
      PERIODS.forEach((p) => {
        const c = cashByPeriod.get(p);
        close[p - 1] = c ? c.balance : 0;
        open[p - 1] = p === 1 ? openingCash : (cashByPeriod.get(p - 1)?.balance || 0);
      });
      balanceLines.forEach((b) => {
        const vals = b.key === 'OPENING_CASH' ? open : close;
        out.push({
          kind: 'balance', key: b.key, label: b.label, emphasis: !!b.emphasis,
          values: vals,
          // A balance's "year" figure is the CLOSING position, never the sum of
          // twelve closing balances — which would be a number with no meaning.
          total: b.key === 'OPENING_CASH' ? openingCash : (cashByPeriod.get(12)?.balance || 0),
          isBalance: true,
        });
      });
    }

    return out;
  }, [lines, sections, subtotals, balanceLines, cashByPeriod, openingCash]);

  const display = useMemo(() => buildStatement(collapsed), [buildStatement, collapsed]);

  const netMovement = useMemo(() => {
    const row = display.find((r) => r.key === 'NET_MOVEMENT');
    return row ? row.total : 0;
  }, [display]);

  const closingCash = cashByPeriod.get(12)?.balance || 0;
  const lowestCash = useMemo(() => {
    if (!cashByPeriod.size) return null;
    let low = { p: null, v: Infinity };
    PERIODS.forEach((p) => {
      const v = cashByPeriod.get(p)?.balance;
      if (v !== undefined && v < low.v) low = { p, v };
    });
    return low.p ? low : null;
  }, [cashByPeriod]);

  // ---- Columns -------------------------------------------------------------
  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  const FIXED = 1;

  const gcols = useMemo(() => {
    const dyn = visibleCols.map((c) => {
      if (c.kind === 'year') return { id: 'YEAR', title: `${yearExpanded ? '▾' : '▸'} Full Year`, width: 132 };
      if (c.kind === 'quarter') return { id: `Q${c.q}`, title: `${expandedQuarters[c.q] ? '▾' : '▸'} Q${c.q}`, width: 112 };
      return { id: `P${c.period}`, title: periodLabel(c.period, fyStartMonth), width: 94 };
    });
    return [
      { id: 'label', title: 'Cash Flow', width: 340, grow: 1 },
      ...dyn,
      ...(yearExpanded ? [{ id: 'total', title: 'Total', width: 132 }] : []),
    ];
  }, [visibleCols, yearExpanded, expandedQuarters, fyStartMonth]);

  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = display[row];
    if (!r) return gcell.text('', { readonly: true });

    const theme = r.kind === 'section' ? T_SECTION
      : r.kind === 'balance' ? T_BALANCE
        : r.kind === 'subtotal'
          ? (r.grand ? (r.total >= 0 ? T_GRAND : T_GRAND_NEG) : T_SUBTOTAL)
          : undefined;

    if (id === 'label') {
      if (r.kind === 'section') {
        return gcell.text(`${collapsed[r.key] ? '▸' : '▾'} ${r.label.toUpperCase()}`, { readonly: true, theme });
      }
      if (r.kind === 'subtotal' || r.kind === 'balance') {
        return gcell.text(r.label.toUpperCase(), { readonly: true, theme });
      }
      return gcell.text(`  ${r.label}`, { readonly: true });
    }

    if (id === 'total') {
      return gcell.number(r.total, { display: fmtR(r.total), readonly: true, theme: theme || T_YEAR });
    }

    const d = visibleCols[col - FIXED];
    if (!d) return gcell.text('', { readonly: true, theme });

    // A BALANCE IS NOT SUMMED ACROSS PERIODS. Quarter and year columns on the
    // cash lines show the CLOSING balance of the last period in the range;
    // adding three closing balances together would be meaningless and would
    // look plausible, which is worse.
    let v;
    if (r.isBalance) {
      if (d.kind === 'month') v = r.values[d.period - 1] || 0;
      else if (r.key === 'OPENING_CASH') {
        const first = d.periods[0];
        v = first === 1 ? openingCash : (cashByPeriod.get(first - 1)?.balance || 0);
      } else {
        v = cashByPeriod.get(d.periods[d.periods.length - 1])?.balance || 0;
      }
    } else {
      v = d.kind === 'month' ? (r.values[d.period - 1] || 0) : sumOf(r, d.periods);
    }

    return gcell.number(v, {
      display: fmtR(v),
      readonly: true,
      theme: theme || (d.kind === 'month' ? undefined : T_YEAR),
    });
  }, [colIds, display, visibleCols, collapsed, FIXED, cashByPeriod, openingCash]);

  const displayRef = useRef([]);
  displayRef.current = display;

  const onCellClicked = useCallback(([col, row]) => {
    if (col !== 0) return;
    const r = displayRef.current[row];
    if (r?.kind === 'section') setCollapsed((prev) => ({ ...prev, [r.key]: !prev[r.key] }));
  }, []);

  const onHeaderClicked = useCallback((colIndex) => {
    const id = colIds[colIndex];
    if (id === 'YEAR') toggleYear();
    else if (/^Q\d+$/.test(id)) toggleQuarter(Number(id.slice(1)));
  }, [colIds, toggleYear, toggleQuarter]);

  const sectionKeys = useMemo(() => sections.map((s) => s.key), [sections]);
  const allCollapsed = sectionKeys.every((k) => collapsed[k] || !lines.some((l) => l.section === k));

  // ---- Everything wrong with the numbers, as one list ----------------------
  const issues = useMemo(() => {
    const out = [];
    if (error) {
      out.push({ id: 'load', level: 'error', title: 'The cash flow could not be loaded', body: error });
    }

    // The single most likely reason this page reads as nonsense, checked
    // explicitly rather than inferred from a zero somewhere in the middle.
    if (!data.assumptions) {
      out.push({
        id: 'no-wc', level: 'error',
        title: 'No working capital assumptions have been set',
        body: 'Debtor days, creditor days, the tax rate and its payment lag all come from one row per version. Without it the working capital and tax lines are computed against nothing.',
        fix: 'Set them on Finance › Working Capital.', route: '/finance/working-capital',
      });
    } else {
      const a = data.assumptions;
      const missing = [];
      if (a.debtor_days === null) missing.push('debtor days');
      if (a.creditor_days === null) missing.push('creditor days');
      if (a.tax_rate === null) missing.push('tax rate');
      if (missing.length) {
        out.push({
          id: 'partial-wc', level: 'warn',
          title: `${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} not set`,
          body: 'NULL means not set, not zero — the affected lines are computed as nil rather than being guessed at.',
          fix: 'Set them on Finance › Working Capital.', route: '/finance/working-capital',
        });
      }
    }

    const unpostedTotal = (data.unposted || []).reduce((s, u) => s + u.unposted_amount, 0);
    if (unpostedTotal !== 0) {
      out.push({
        id: 'unposted', level: 'error',
        title: `${fmtR(unpostedTotal)} of budgeted cost posts nowhere`,
        body: 'It has no GL account, so it is absent from the profit this statement starts from — and therefore from the cash flow.',
        detail: (data.unposted || []).map((u) => `${u.module_code} ${fmtR(u.unposted_amount)}`).join(' · '),
        fix: 'Fix on the posting pages under each module’s Budget Setup.',
      });
    }

    // The gap the reconciliation view exists to surface. Not an error: the
    // schedule is the source of cash timing and OPEX is the source of the
    // charge, deliberately. But if they disagree, one of them is wrong.
    if (data.interest && (data.interest.perSchedule || data.interest.inLedger)) {
      const diff = data.interest.inLedger - data.interest.perSchedule;
      if (Math.abs(diff) > 1) {
        out.push({
          id: 'interest', level: 'warn',
          title: `Interest on the debt schedule and finance costs on the P&L differ by ${fmtR(Math.abs(diff))}`,
          body: `The schedule computes ${fmtR(data.interest.perSchedule)}; the ledger carries ${fmtR(data.interest.inLedger)}. The schedule drives cash timing and OPEX drives the charge — that split is deliberate, but the two should still agree in total.`,
          fix: 'Either adjust the OPEX finance cost lines, or correct the debt instruments. Instruments are maintained in bud_debt_instruments — a page for them is not built yet.',
        });
      }
    }

    if (lowestCash && lowestCash.v < 0) {
      out.push({
        id: 'negative-cash', level: 'error',
        title: `Cash goes negative in ${periodLabel(lowestCash.p, fyStartMonth)} (${fmtR(lowestCash.v)})`,
        body: 'The budget as it stands cannot be funded from its own cash generation in every month of the year.',
        fix: 'Either the facility needs to cover it — draw more on an instrument in bud_debt_instruments, for which a page is not built yet — or the plan needs to change.',
      });
    }

    return out;
  }, [error, data.assumptions, data.unposted, data.interest, lowestCash, fyStartMonth]);

  const worstLevel = issues.some((i) => i.level === 'error') ? 'error' : issues.length ? 'warn' : null;
  const issuesRef = useRef(null);
  const goToIssues = () => issuesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // How much of the debtor position rests on a contract rather than a guess.
  const contractShare = useMemo(() => {
    const rows = data.daysSources || [];
    const total = rows.reduce((s, r) => s + r.n, 0);
    if (!total) return null;
    const fromTerms = rows
      .filter((r) => r.days_source === 'CUSTOMER_TERMS' || r.days_source === 'GROUP_TERMS')
      .reduce((s, r) => s + r.n, 0);
    return { total, fromTerms, pct: (fromTerms / total) * 100 };
  }, [data.daysSources]);

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to see the cash flow.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* One line of chrome, then the statement. Everything advisory is below
          the grid; the counter beside the title says it is there. */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/finance')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Finance
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              Cash Flow
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
              Indirect · Group
            </span>
            {worstLevel && (
              <button
                onClick={goToIssues}
                title={issues.map((i) => i.title).join('\n')}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold transition-colors ${
                  worstLevel === 'error'
                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300'}`}
              >
                <TriangleAlert className="w-3 h-3" />
                {issues.length} {issues.length === 1 ? 'issue' : 'issues'}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!loading && display.length > 0 && (
              <>
                <Figure label="Opening cash" value={fmtR(openingCash)} />
                <Figure label="Net movement" value={fmtR(netMovement)}
                        tone={netMovement >= 0 ? 'good' : 'bad'} />
                <Figure label="Closing cash" value={fmtR(closingCash)}
                        sub={lowestCash && lowestCash.v < closingCash
                          ? `low ${fmtR(lowestCash.v)} in ${periodLabel(lowestCash.p, fyStartMonth)}` : ''}
                        tone={closingCash < 0 ? 'bad' : undefined} />
              </>
            )}
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ONE SCROLLBAR ON THIS PAGE, AND IT IS THE PAGE'S. The statement renders
          at its full height and the page scrolls past it to the notes. */}
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : error ? (
            <div className="py-10 px-6 text-center">
              <p className="m-0 text-sm font-semibold text-red-700 dark:text-red-300">The cash flow could not be loaded.</p>
              <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">{error}</p>
              <button className={`${btn} mt-4`} onClick={load}>
                <RefreshCw className="w-3.5 h-3.5" /> Try again
              </button>
            </div>
          ) : display.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              Nothing to show for {selectedVersion} yet. The cash flow starts from the consolidated P&amp;L — if that is
              empty, this will be too.
            </p>
          ) : (
            <MantleDataGrid
              gridId="finance-cashflow"
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={display.length}
              getCellContent={getCellContent}
              onCellClicked={onCellClicked}
              onHeaderClicked={onHeaderClicked}
              freezeColumns={1}
              hint="Click Full Year → quarters → months to drill into the periods. Click a SECTION heading to fold its detail away. Read-only: every figure is derived from the module that produced it."
              toolbar={
                <>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    brackets are cash out
                  </span>
                  <button type="button" onClick={yearExpanded ? collapseAll : expandAll} className={btn}>
                    {yearExpanded ? 'Collapse periods' : 'Expand periods'}
                  </button>
                  <button
                    type="button"
                    className={btn}
                    onClick={() => setCollapsed(allCollapsed
                      ? {}
                      : Object.fromEntries(sectionKeys.map((k) => [k, true])))}
                  >
                    {allCollapsed ? 'Show detail' : 'Sections only'}
                  </button>
                </>
              }
            />
          )}
        </div>

        {/* Below the fold, where footnotes belong. */}
        <div ref={issuesRef} className="scroll-mt-2 space-y-3">
          {issues.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                <TriangleAlert className={`w-4 h-4 ${worstLevel === 'error' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {issues.length} thing{issues.length === 1 ? '' : 's'} to fix before this statement is final
                </h2>
                <span className="text-[11px] text-slate-400">
                  red = a figure is missing or the plan cannot be funded · amber = a figure may be wrong
                </span>
              </div>
              <ul className="m-0 p-0 list-none divide-y divide-slate-100 dark:divide-slate-700/60">
                {issues.map((i) => (
                  <li key={i.id} className={`px-4 py-2.5 border-l-4 ${
                    i.level === 'error' ? 'border-l-red-500 bg-red-50/40 dark:bg-red-500/5'
                      : 'border-l-amber-400 bg-amber-50/40 dark:bg-amber-500/5'}`}>
                    <p className="m-0 text-xs font-semibold text-slate-800 dark:text-slate-100">{i.title}</p>
                    <p className="m-0 mt-0.5 text-xs text-slate-600 dark:text-slate-300">{i.body}</p>
                    {i.detail && (
                      <p className="m-0 mt-1 text-[11px] font-mono text-slate-500 dark:text-slate-400 break-words">{i.detail}</p>
                    )}
                    {i.fix && <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">{i.fix}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* The assumptions this statement rests on, stated on it. A cash flow
              whose debtor days live on another page is a cash flow nobody can
              check. */}
          {data.assumptions && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
              <h2 className="m-0 mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                What this statement assumes
              </h2>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                <Assume label="Debtor days" value={data.assumptions.debtor_days} suffix=" days"
                        note={contractShare
                          ? `${contractShare.fromTerms} of ${contractShare.total} customers resolve from their own terms (${contractShare.pct.toFixed(0)}%) — the rest use this fallback`
                          : ''} />
                <Assume label="Creditor days" value={data.assumptions.creditor_days} suffix=" days"
                        note="Suppliers with payment terms on record use those instead" />
                <Assume label="Tax rate" value={data.assumptions.tax_rate} suffix="%" />
                <Assume label="Tax paid" value={data.assumptions.tax_payment_lag_periods}
                        suffix={data.assumptions.tax_payment_lag_periods === 1 ? ' period later' : ' periods later'} />
                <Assume label="VAT" value={data.assumptions.vat_enabled ? 'Modelled' : 'Excluded'} raw />
              </div>
            </div>
          )}

          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>Indirect method.</strong> Profit before tax, with depreciation and finance costs added back
            because neither moved cash in the month it was charged, then the cash tied up in working capital, then
            investing and financing. Cash in is positive and cash out is in brackets, so every subtotal is the plain
            sum of the lines above it.
          </p>
          <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
            <strong>Group level, and deliberately so.</strong> Cash is managed centrally and sites do not have their
            own bank accounts, so there is no site breakdown here — a per-site cash position would be a number nobody
            could act on. Depreciation is added back from the ledger rather than recomputed from the asset register,
            so a journal against a depreciation account moves this statement and the P&amp;L together.
          </p>
        </div>
      </div>
    </div>
  );
}

const Figure = ({ label, value, sub, tone }) => {
  const cls = tone === 'bad'
    ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300'
    : tone === 'good'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';
  return (
    <div className={`inline-flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border ${cls}`}>
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</span>
      <span className="text-sm font-bold tabular-nums">{value}</span>
      {sub && <span className="text-[10px] opacity-70">{sub}</span>}
    </div>
  );
};

// NULL reads as "not set", never as zero — the whole point of the nullable
// columns behind this page.
const Assume = ({ label, value, suffix = '', note, raw }) => (
  <span className="inline-flex items-baseline gap-1.5">
    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    {value === null || value === undefined ? (
      <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">not set</span>
    ) : (
      <span className="text-xs font-semibold tabular-nums text-slate-800 dark:text-slate-100">
        {raw ? value : `${value}${suffix}`}
      </span>
    )}
    {note && <span className="text-[10px] text-slate-400">· {note}</span>}
  </span>
);
